import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Mail, Lock, User, FileText, Globe, Calendar, Users, Shield, Key, CheckCircle2, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import Logo from '../assets/logo.svg';
import InsuranceNavbar from '../components/InsuranceNavbar';
import InsuranceFooter from '../components/InsuranceFooter';

const Register: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();

  // Step 1: Corporate Account Setup
  const [companyName, setCompanyName] = useState('');
  const [operatingCountry, setOperatingCountry] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [corporateEmail, setCorporateEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 2: Partnership Scope & Preferences
  const [subscriptionPlan, setSubscriptionPlan] = useState('');
  const [pilotGroupSize, setPilotGroupSize] = useState('');
  const [insuranceTypes, setInsuranceTypes] = useState<string[]>([]);
  const [goLiveDate, setGoLiveDate] = useState('');

  const insuranceCompanies = [
    'Golden Falcon Insurance',
    'Emirates Health Shield',
    'National Medical Coverage',
    'Arabian Healthcare Protection',
    'Gulf Insurance Group',
    'United Medical Insurers',
    'Premium Health Partners',
    'Regional Care Insurance'
  ];

  const countries = [
    'United Arab Emirates',
    'Saudi Arabia',
    'Qatar',
    'Kuwait',
    'Bahrain',
    'Oman',
    'Jordan',
    'Egypt'
  ];

  const subscriptionPlans = [
    { value: 'analytics', label: 'HIRSTO Analytics - Basic fraud detection and analytics' },
    { value: 'efficiency', label: 'HIRSTO Efficiency - Full automation with STP' },
    { value: 'strategic', label: 'HIRSTO Strategic Partner - Complete enterprise solution' }
  ];

  const insuranceTypeOptions = [
    'Individual Health Insurance',
    'B2B Health Insurance',
    'Family Health Plans',
    'Corporate Group Plans',
    'Senior Care Plans',
    'Dental Insurance',
    'Vision Care Insurance'
  ];

  const handleInsuranceTypeToggle = (type: string) => {
    setInsuranceTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long!');
      return;
    }

    toast.success('Corporate account information saved!');
    setCurrentStep(2);
  };

  const handleStep2Submit = (e: React.FormEvent) => {
    e.preventDefault();

    if (insuranceTypes.length === 0) {
      toast.error('Please select at least one insurance type!');
      return;
    }

    toast.success('Partnership preferences saved!');
    setCurrentStep(3);
  };

  const generateApiKey = () => {
    const key = `HIRSTO_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    setGeneratedApiKey(key);
    toast.success('API Key generated successfully!');
  };

  const handleFinishRegistration = () => {
    if (!agreedToTerms) {
      toast.error('Please agree to the terms of use!');
      return;
    }

    if (!generatedApiKey) {
      toast.error('Please generate your API key first!');
      return;
    }

    setIsLoading(true);
    setTimeout(() => {
      toast.success('Registration complete! Welcome to HIRSTO!');
      navigate('/login');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <InsuranceNavbar />

      {/* Registration Content */}
      <div className="min-h-screen py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <img src={Logo} alt="HIRSTO" className="w-48 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-[#03045E] mb-3">
              Insurance Partner Registration
            </h1>
            <p className="text-lg text-[#5C7A99]">
              Join HIRSTO's network of leading insurance providers
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {[
              { num: 1, label: 'Corporate Account' },
              { num: 2, label: 'Partnership Details' },
              { num: 3, label: 'API Integration' }
            ].map((step, idx) => (
              <React.Fragment key={step.num}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                      currentStep >= step.num
                        ? 'bg-[#0077B6] text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {currentStep > step.num ? <CheckCircle2 className="w-6 h-6" /> : step.num}
                  </div>
                  <p className={`text-sm mt-2 font-medium ${
                    currentStep >= step.num ? 'text-[#0077B6]' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </p>
                </div>
                {idx < 2 && (
                  <div
                    className={`w-24 h-1 mx-4 transition-all ${
                      currentStep > step.num ? 'bg-[#0077B6]' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            {/* Step 1: Corporate Account Setup */}
            {currentStep === 1 && (
              <div>
                <div className="bg-[#03045E] px-8 py-8">
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Corporate Account Setup
                  </h2>
                  <p className="text-white/80 text-sm">
                    Provide your insurance company's official information
                  </p>
                </div>

                <form onSubmit={handleStep1Submit} className="px-8 py-10 space-y-6">
                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Official Insurance Company Name *
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                      <select
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                        required
                      >
                        <option value="">Select your company from pre-approved list</option>
                        {insuranceCompanies.map(company => (
                          <option key={company} value={company}>{company}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Operating Country */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Primary Operating Country *
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                      <select
                        value={operatingCountry}
                        onChange={(e) => setOperatingCountry(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                        required
                      >
                        <option value="">Select operating country</option>
                        {countries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* License Number */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Commercial License Number *
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                      <input
                        type="text"
                        value={licenseNumber}
                        onChange={(e) => setLicenseNumber(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                        placeholder="e.g., UAE-INS-2024-12345"
                        required
                      />
                    </div>
                    <p className="text-xs text-[#5C7A99] mt-2">
                      This will be used as your unique company identifier
                    </p>
                  </div>

                  {/* Contact Person */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Primary Contact Person *
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                      <input
                        type="text"
                        value={contactPerson}
                        onChange={(e) => setContactPerson(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                        placeholder="e.g., Mr. Mansour Al-Shamsi"
                        required
                      />
                    </div>
                  </div>

                  {/* Corporate Email */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Corporate Email (Username) *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                      <input
                        type="email"
                        value={corporateEmail}
                        onChange={(e) => setCorporateEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                        placeholder="mansour.s@golden-falcon.ae"
                        required
                      />
                    </div>
                    <p className="text-xs text-[#5C7A99] mt-2">
                      Must be official company email domain
                    </p>
                  </div>

                  {/* Password */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                        Password *
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                        Confirm Password *
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#0077B6] text-white py-4 rounded-xl font-semibold text-base hover:bg-[#005a8a] transition-all flex items-center justify-center gap-2 group mt-8"
                  >
                    <span>Continue to Partnership Details</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              </div>
            )}

            {/* Step 2: Partnership Scope & Preferences */}
            {currentStep === 2 && (
              <div>
                <div className="bg-[#03045E] px-8 py-8">
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Partnership Scope & Preferences
                  </h2>
                  <p className="text-white/80 text-sm">
                    Configure your HIRSTO integration preferences
                  </p>
                </div>

                <form onSubmit={handleStep2Submit} className="px-8 py-10 space-y-6">
                  {/* Subscription Plan */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-3">
                      Approved Subscription Plan *
                    </label>
                    <div className="space-y-3">
                      {subscriptionPlans.map(plan => (
                        <label
                          key={plan.value}
                          className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            subscriptionPlan === plan.value
                              ? 'border-[#0077B6] bg-[#0077B6]/5'
                              : 'border-gray-200 hover:border-[#0077B6]/30'
                          }`}
                        >
                          <input
                            type="radio"
                            name="plan"
                            value={plan.value}
                            checked={subscriptionPlan === plan.value}
                            onChange={(e) => setSubscriptionPlan(e.target.value)}
                            className="mt-1 text-[#0077B6] focus:ring-[#0077B6]"
                            required
                          />
                          <span className="ml-3 text-sm text-gray-900">{plan.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Pilot Group Size */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Number of Insured for Pilot Phase *
                    </label>
                    <div className="relative">
                      <Users className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                      <input
                        type="number"
                        value={pilotGroupSize}
                        onChange={(e) => setPilotGroupSize(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                        placeholder="e.g., 5000"
                        min="100"
                        required
                      />
                    </div>
                    <p className="text-xs text-[#5C7A99] mt-2">
                      This determines infrastructure preparation requirements
                    </p>
                  </div>

                  {/* Insurance Types */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-3">
                      Primary Insurance Types (Select all that apply) *
                    </label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {insuranceTypeOptions.map(type => (
                        <label
                          key={type}
                          className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            insuranceTypes.includes(type)
                              ? 'border-[#0077B6] bg-[#0077B6]/5'
                              : 'border-gray-200 hover:border-[#0077B6]/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={insuranceTypes.includes(type)}
                            onChange={() => handleInsuranceTypeToggle(type)}
                            className="text-[#0077B6] focus:ring-[#0077B6] rounded"
                          />
                          <span className="ml-3 text-sm text-gray-900">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Go-Live Date */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
                      Target Go-Live Date *
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0077B6]/50" />
                      <input
                        type="date"
                        value={goLiveDate}
                        onChange={(e) => setGoLiveDate(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#E5E7EB] hover:border-[#0077B6]/50 focus:ring-0 focus:border-[#0077B6] transition-all bg-white text-gray-900"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <p className="text-xs text-[#5C7A99] mt-2">
                      Expected date for actual operation start
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 bg-gray-100 text-gray-700 py-4 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-[#0077B6] text-white py-4 rounded-xl font-semibold hover:bg-[#005a8a] transition-all flex items-center justify-center gap-2 group"
                    >
                      <span>Continue to API Setup</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 3: Technical Key Generation */}
            {currentStep === 3 && (
              <div>
                <div className="bg-[#03045E] px-8 py-8">
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Technical Key Generation
                  </h2>
                  <p className="text-white/80 text-sm">
                    Generate API credentials for technical integration
                  </p>
                </div>

                <div className="px-8 py-10 space-y-6">
                  {/* API Key Generation */}
                  <div className="bg-gradient-to-br from-[#0077B6]/5 to-[#235DFF]/5 rounded-2xl p-6 border border-[#0077B6]/20">
                    <div className="flex items-start gap-4 mb-4">
                      <Key className="w-6 h-6 text-[#0077B6] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-[#03045E] mb-2">
                          Generate API Key
                        </h3>
                        <p className="text-sm text-[#5C7A99] mb-4">
                          Click below to generate your unique API credentials for HIRSTO integration. 
                          Your IT team will use this key to begin technical integration.
                        </p>
                        <button
                          onClick={generateApiKey}
                          disabled={!!generatedApiKey}
                          className="bg-[#0077B6] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#005a8a] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <Key className="w-5 h-5" />
                          {generatedApiKey ? 'API Key Generated' : 'Generate API Key'}
                        </button>
                      </div>
                    </div>

                    {generatedApiKey && (
                      <div className="mt-4 p-4 bg-white rounded-xl border border-[#0077B6]/30">
                        <p className="text-xs text-[#5C7A99] mb-2 font-semibold">Your API Key:</p>
                        <code className="block p-3 bg-gray-50 rounded-lg text-sm text-[#03045E] font-mono break-all">
                          {generatedApiKey}
                        </code>
                        <p className="text-xs text-red-600 mt-2 flex items-start gap-2">
                          <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>Save this key securely. It will not be shown again.</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* API Documentation */}
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-[#03045E] mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#0077B6]" />
                      API Documentation
                    </h3>
                    <p className="text-sm text-[#5C7A99] mb-4">
                      Your IT team must review the integration documentation before beginning:
                    </p>
                    <div className="space-y-2">
                      <a
                        href="#"
                        className="block p-3 bg-white rounded-lg border border-gray-200 hover:border-[#0077B6]/50 transition-all text-sm text-[#0077B6] font-semibold"
                      >
                        → API Integration Guide (PDF)
                      </a>
                      <a
                        href="#"
                        className="block p-3 bg-white rounded-lg border border-gray-200 hover:border-[#0077B6]/50 transition-all text-sm text-[#0077B6] font-semibold"
                      >
                        → RESTful Endpoints Documentation
                      </a>
                      <a
                        href="#"
                        className="block p-3 bg-white rounded-lg border border-gray-200 hover:border-[#0077B6]/50 transition-all text-sm text-[#0077B6] font-semibold"
                      >
                        → Data Exchange Schema (JSON)
                      </a>
                    </div>
                  </div>

                  {/* Terms Agreement */}
                  <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
                    <label className="flex items-start gap-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="mt-1 text-[#0077B6] focus:ring-[#0077B6] rounded w-5 h-5"
                      />
                      <div>
                        <p className="text-sm text-gray-900 font-semibold mb-2">
                          Terms of Use Approval (Required) *
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          I acknowledge that I am authorized to activate technical keys for the insurance company 
                          and agree to HIRSTO service terms and privacy conditions related to claims data exchange. 
                          I understand that all data will be handled in compliance with applicable regulations including 
                          GDPR and local healthcare data protection laws.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="flex-1 bg-gray-100 text-gray-700 py-4 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleFinishRegistration}
                      disabled={!agreedToTerms || !generatedApiKey || isLoading}
                      className="flex-1 bg-gradient-to-r from-[#0077B6] to-[#235DFF] text-white py-4 rounded-xl font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
                    >
                      {isLoading ? (
                        <span>Completing Registration...</span>
                      ) : (
                        <>
                          <span>Finish & Login</span>
                          <CheckCircle2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        </>
                      )}
                    </button>
                  </div>

                  {/* Help Text */}
                  <div className="text-center pt-4">
                    <p className="text-xs text-[#5C7A99]">
                      Need assistance? Contact our integration support team at{' '}
                      <a href="mailto:support@hirsto.com" className="text-[#0077B6] font-semibold">
                        support@hirsto.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Already have account */}
          <div className="text-center mt-8">
            <p className="text-sm text-[#5C7A99]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0077B6] hover:text-[#005a8a] font-semibold transition-colors">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <InsuranceFooter />
    </div>
  );
};

export default Register;
